import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArchiveRestore, FolderClosed, Search, Trash2 } from 'lucide-react';
import type { AssetKind } from '@shared/types';
import { Button, CenteredSpinner, EmptyState, ErrorState } from '@/components/ui';
import { useToast } from '@/components/Toasts';
import { KIND_ICON, KIND_TINT, cn, formatDate } from '@/lib/format';
import { t } from '@/lib/i18n';
import { useMe, useRecycleBin, useRestore } from '@/lib/queries';
import type { RecycleBinItem } from '@/lib/api';

function kindOf(item: RecycleBinItem): AssetKind {
  if (item.isFolder) return 'folder';
  const m = item.mimetype ?? '';
  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('video/')) return 'video';
  if (m.startsWith('audio/')) return 'audio';
  if (m === 'application/pdf' || m.includes('word') || m.includes('sheet') || m.includes('presentation')) return 'document';
  if (m.includes('zip')) return 'archive';
  if (m.startsWith('text/')) return 'text';
  return 'unknown';
}

/**
 * The recycle bin. Deleted files and folders wait here and can be put back
 * where they were. Before this page the only way back was the 10-second Undo
 * on the toast. Users see what they deleted; admins see everyone's deletions.
 */
export function RecycleBinPage() {
  const { data, isPending, error, refetch } = useRecycleBin();
  const { data: me } = useMe();
  const restore = useRestore();
  const toast = useToast();
  const [q, setQ] = useState('');
  // Restored in this visit: kept on screen (the refetch drops them) with an
  // "Open" link to where they are now.
  const [done, setDone] = useState<Record<number, { item: RecycleBinItem; assetId: number | null }>>({});
  const [busy, setBusy] = useState<number | null>(null);

  const rows = useMemo(() => {
    const live = data?.items ?? [];
    const kept = Object.values(done).map((d) => d.item).filter((i) => !live.some((l) => l.id === i.id));
    const all = [...live, ...kept].sort((a, b) => b.deletedAt.localeCompare(a.deletedAt) || b.id - a.id);
    const needle = q.trim().toLowerCase();
    return needle ? all.filter((i) => `${i.folder}/${i.name}`.toLowerCase().includes(needle)) : all;
  }, [data, done, q]);

  if (isPending) return <CenteredSpinner label={t('recycleBinPage.loading')} />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  async function doRestore(item: RecycleBinItem) {
    setBusy(item.id);
    try {
      const res = await restore.mutateAsync(item.id);
      setDone((d) => ({ ...d, [item.id]: { item, assetId: res?.id ?? null } }));
      toast.push({ message: t('recycleBinPage.restored', { name: item.name }) });
    } catch {
      toast.push({ message: t('recycleBinPage.restoreFailed', { name: item.name }), tone: 'error' });
    } finally {
      setBusy(null);
    }
  }

  const total = data?.items.length ?? 0;

  return (
    <div className="scroll-thin h-full overflow-y-auto">
      <div className="w-full px-6 py-5">
        <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-lg font-semibold text-strong">
              <Trash2 className="size-5 text-brand-500" /> {t('recycleBinPage.title')}
            </h1>
            <p className="mt-0.5 text-xs text-muted">
              {me?.isAdmin ? t('recycleBinPage.hintAdmin') : t('recycleBinPage.hint')}
            </p>
          </div>
          {total > 0 && (
            <label className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-faint" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('recycleBinPage.filter')}
                className="surface-card h-9 w-64 rounded-lg border line pl-8 pr-3 text-sm text-strong placeholder:text-faint focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </label>
          )}
        </header>

        {rows.length === 0 ? (
          <EmptyState
            icon={<Trash2 />}
            title={total === 0 ? t('recycleBinPage.empty') : t('recycleBinPage.noMatch')}
            description={total === 0 ? t('recycleBinPage.emptyHint') : undefined}
          />
        ) : (
          <ul className="overflow-hidden rounded-xl border line surface-card">
            {rows.map((item) => {
              const kind = kindOf(item);
              const Icon = item.isFolder ? FolderClosed : KIND_ICON[kind];
              const restored = item.id in done;
              return (
                <li key={item.id} className="flex items-center gap-3 border-t line px-4 py-2.5 first:border-t-0">
                  <Icon className={cn('size-5 shrink-0', KIND_TINT[kind])} aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className={cn('truncate text-sm font-medium', restored ? 'text-muted' : 'text-strong')} title={item.name}>
                      {item.name}
                      {item.isFolder && item.childCount > 0 && (
                        <span className="ml-1.5 text-xs font-normal text-muted">
                          {t('recycleBinPage.nItems', { n: item.childCount })}
                        </span>
                      )}
                    </p>
                    <p className="truncate text-xs text-muted" title={item.folder}>
                      {item.folder} · {t('recycleBinPage.deletedOn', { date: formatDate(item.deletedAt) })}
                      {me?.isAdmin && item.deletedBy ? ` · ${item.deletedBy}` : ''}
                    </p>
                  </div>
                  {restored ? (
                    done[item.id]?.assetId != null && !item.isFolder ? (
                      <Link
                        to="/assets/$assetId"
                        params={{ assetId: done[item.id]!.assetId! }}
                        className="text-sm font-medium text-brand-600 hover:underline"
                      >
                        {t('recycleBinPage.open')}
                      </Link>
                    ) : (
                      <span className="text-sm text-muted">{t('recycleBinPage.restoredShort')}</span>
                    )
                  ) : (
                    <Button
                      onClick={() => void doRestore(item)}
                      loading={busy === item.id}
                      disabled={busy !== null && busy !== item.id}
                    >
                      <ArchiveRestore className="size-4" /> {t('recycleBinPage.restore')}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
